import { LockClosedIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";

export enum StepStatus {
	Error,
	Locked,
	Current,
	Complete,
	Upcoming,
}

interface StepsModel {
	id: number;
	name: string;
	status: StepStatus;
}

interface Props {
	activeStep?: StepsModel;
	steps: StepsModel[];
	setStep: (stepId: number) => void;
	useLargeMediaBreakpoint: boolean;
	className?: string;
}

export default function StepProgressBar(props: Props) {
	return (
		<nav aria-label="Progress">
			<ol
				role="list"
				className={`${props.useLargeMediaBreakpoint ? "lg:flex lg:divide-y-0" : "md:flex md:divide-y-0"} divide-y divide-gray-300 rounded-md border border-gray-300 mt-4`}>
				{props.steps.map((step, stepIdx) => (
					<li
						key={step.name}
						className={`${props.useLargeMediaBreakpoint ? "lg:flex lg:flex-1" : "md:flex md:flex-1"} relative`}>
						{step.status === StepStatus.Complete ? (
							<Step
								activeStep={props.activeStep}
								step={step}
								changeStep={() => props.setStep(step.id)}
								textClassname="text-gray-900"
								iconClassname="bg-primary-700 group-hover:bg-primary-800"
							/>
						) : step.status === StepStatus.Current ? (
							<Step
								activeStep={props.activeStep}
								step={step}
								changeStep={() => props.setStep(step.id)}
								textClassname="text-primary-700"
								iconClassname="border-2 border-primary-700"
							/>
						) : step.status === StepStatus.Error ? (
							<Step
								activeStep={props.activeStep}
								step={step}
								changeStep={() => props.setStep(step.id)}
								textClassname="text-red-600"
								iconClassname="border-2 border-red-600"
							/>
						) : step.status === StepStatus.Locked ? (
							<Step
								activeStep={props.activeStep}
								step={step}
								changeStep={() => {}}
								textClassname="text-gray-900"
								iconClassname="bg-primary-700 group-hover:bg-primary-800"
							/>
						) : (
							<Step
								activeStep={props.activeStep}
								step={step}
								changeStep={() => props.setStep(step.id)}
								textClassname="text-gray-500 group-hover:text-gray-900"
								iconClassname="border-2 border-gray-300 group-hover:border-gray-400"
							/>
						)}

						{stepIdx !== props.steps.length - 1 ? (
							<>
								{/* Arrow separator for the media breakpoint and up */}
								<div
									aria-hidden="true"
									className={`${props.useLargeMediaBreakpoint ? "lg:block" : "md:block"} absolute right-0 top-0 hidden h-full w-5`}>
									<svg
										fill="none"
										viewBox="0 0 22 80"
										preserveAspectRatio="none"
										className="size-full text-gray-300">
										<path
											d="M0 -2L20 40L0 82"
											stroke="currentcolor"
											vectorEffect="non-scaling-stroke"
											strokeLinejoin="round"
										/>
									</svg>
								</div>
							</>
						) : null}
					</li>
				))}
			</ol>
		</nav>
	);
}

interface StepProps {
	activeStep?: StepsModel;
	step: StepsModel;
	changeStep: (id: number) => void;
	textClassname?: string;
	iconClassname?: string;
}

function Step(props: StepProps) {
	return (
		<a
			onClick={() => props.changeStep(props.step.id)}
			className="group flex w-full items-center">
			<span
				className={`${props.step.status === StepStatus.Locked ? "hover:cursor-not-allowed" : "hover:cursor-pointer"} flex items-center px-6 py-2 text-sm font-medium`}>
				<span
					className={`${props.iconClassname} flex size-9 shrink-0 items-center justify-center rounded-full`}>
					{props.step.status === StepStatus.Complete ? (
						<CheckIcon aria-hidden="true" className="size-6 text-white" />
					) : props.step.status === StepStatus.Locked ? (
						<LockClosedIcon aria-hidden="true" className="size-6 text-white" />
					) : props.step.status === StepStatus.Error ? (
						<span aria-hidden="true" className="text-xl text-red-600">
							!
						</span>
					) : (
						<span className={`${props.textClassname}`}>{props.step.id}</span>
					)}
				</span>
				<span
					className={`${props.textClassname} ${props.activeStep === props.step ? "underline underline-offset-4 decoration-inherit text-primary-700" : ""} ml-4 text-sm font-medium`}>
					{props.step.name}
				</span>
			</span>
		</a>
	);
}
